/**
 * ExistingEventGestures — the shared existing-item gesture set
 * (Move / Resize / ClickToOpenDetails / ContextMenu) plus the single
 * surface-level Dismiss gesture.
 *
 * ## The lifecycle of a gesture
 *
 * A gesture's life has four phases; popovers add two more.
 *
 * 1. **Engage** (`canHandle` at pointer-down). The gesture claims the
 *    interaction: the renderer's `ItemGuard` narrows the span's item
 *    to its own class; weak-hold state is set for sub-threshold
 *    motion. No world side-effects are permitted in this phase except
 *    `host.toast` (debug). A gesture that needs room for its UI
 *    (context menu) calls `host.dismissOthers()` — it never reaches
 *    into another system's menus.
 * 2. **Track** (pointer-move stream). The first real movement
 *    dismisses the item's OWN popover once (`item.dismissDetails()`,
 *    once-guarded — dismissal may clear the ghost, so it must precede
 *    painting). From then on, tracking paints intent exclusively
 *    through `host.ghost` — never popovers, never other renderers.
 * 3. **Settle** (pointer-up). The gesture computes the final range
 *    and calls `item.persistTimeChange(range)`. The delegate may
 *    clear the ghost EARLY, inside persist, before the network
 *    round-trip (optimistic settle — the settled span replaces the
 *    ghost without a flash). The gesture's `finally` `host.ghost.clear()`
 *    is the idempotent backstop.
 * 4. **Conclude** (`onFinished`). Re-show details if they were open
 *    before the drag (re-guarded against the persisted item), release
 *    weak holds, reset the cursor, null the state.
 *
 * Popovers ride on top: **Reveal** (`RendererPopover.show` = make
 * room + present + announce, one call) and **Dismiss** (explicit
 * dismiss, the empty-column `Dismiss` gesture, the coordinator's
 * click-outside sweep, and another system's Reveal all converge on
 * the popover's single registered `GestureSystemState.dismiss`).
 *
 * ## The well-behaved custom gesture contract
 *
 * Extend `CalendarGesture.Default<GestureInfo>`, guard to your own
 * item class, and interact with the world ONLY via your
 * `RendererGestureHost`. Coordination, whitelisting, mutual
 * exclusion, and dismissal are then automatic. A renderer that
 * touches the presenter, the coordinator, or another renderer's
 * state directly is the bug.
 *
 * Each gesture is a `CalendarGesture.Default<GestureInfo>` subclass
 * routed through the column-level `CalendarGesture.Manager`.
 * Cross-column drag "just works": each `CalendarColumn`'s
 * `onPointerMove` dispatches with the receiving column's `windowed`,
 * and the manager routes by pointerId — `state.holdingWeakly`
 * releases on a date change. Gesture classes are shared via
 * COMPOSITION: generic over the item surface each needs and
 * instantiated per renderer with a type guard; a renderer that
 * doesn't support resize never constructs a `Resize` (the
 * registration helper derives Move's `absorbEdgeHits` from that).
 */

import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import {
  DateFragment,
  DateTime,
  Duration,
  DurationTime,
  Utc,
} from "@tento-chrono";
import {
  CalendarGesture,
  GestureItem,
} from "@tento-ui/components/calendar/calendar-gesture";

import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import type {
  CalendarGestureHost,
  RendererGestureHost,
} from "@tento-lona/sheets-ui/sheet/gestures/renderer-gesture-host.ts";
import type {
  HasContextMenu,
  Movable,
  OpensDetails,
} from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";

import { CalendarGestureState } from "./calendar-gesture-state.ts";
import { snapToNearestQuarterHour } from "./quarter-hour-snap.ts";
import { ghostContainsPointerEvent } from "@tento-lona/sheets-ui/sheet/gestures/drag-ghost-service.ts";


/**
 * The drag, open and context-menu gestures on existing items that renderers
 * share.
 */
export namespace ExistingEventGestures {
  /** Narrows a span's gesture item (or a persisted result) to one
   *  renderer's item class. Implementations are `instanceof` checks. */
  export type ItemGuard<I> = (item: object) => item is I & GestureItem;

  /**
   * Register the standard span gesture trio for one renderer's item
   * type: Move, optional Resize, ClickToOpenDetails. `absorbEdgeHits`
   * is DERIVED from the resize declaration — when no Resize instance
   * exists for the item type, Move absorbs the cell layer's ±4px edge
   * hits so they can't dead-zone. Renderers with a context menu add
   * `new ContextMenu({...})` themselves (it needs the wider
   * `HasContextMenu` item surface).
   */
  export function registerSpanGestures<I extends Movable & OpensDetails>(
    manager: CalendarGesture.Manager<GestureInfo>,
    host: RendererGestureHost,
    guard: ItemGuard<I>,
    opts: { resize: boolean },
  ): void {
    manager.addGesture(
      new Move({ guard, host, absorbEdgeHits: !opts.resize }),
    );
    if (opts.resize) {
      manager.addGesture(new Resize({ guard, host }));
    }
    manager.addGesture(new ClickToOpenDetails({ guard, host }));
  }

  export class ContextMenu<
    I extends HasContextMenu,
  > extends CalendarGesture.Default<GestureInfo> {
    readonly identifier: string = "context-menu-gesture";

    #weak: boolean = false;

    readonly #opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      };
    constructor(
      opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      },
    ) {
      super();
      this.#opts = opts;
    }

    guard(info: GestureInfo): boolean {
      return info.item != null && this.#opts.guard(info.item);
    }

    canHandle(e: CalendarGesture.Event<GestureInfo>): boolean {
      const handled =
        e.matchOpt({
          pointerDownContextMenu: () => true,
        }) ?? false;
      if (handled) this.#opts.host.toast("Context Menu");
      if (handled) {
        // Make room for the menu — other systems' popovers/menus go
        // away; this gesture never touches their handles directly.
        this.#opts.host.dismissOthers();
      }
      return handled;
    }

    onPointerDownContextMenu({
      info,
      offset,
    }: CalendarGesture.Event.PointerDownContextMenu<GestureInfo>): { finished: boolean } {
      const item = info.item;
      if (item == null || !this.#opts.guard(item)) return { finished: true };

      this.#weak = true;
      this.#lastItem = item;

      item.showContextMenu(
        { rowId: info.rowId, column: info.column, windowed: info.windowed },
        offset,
      );
      return { finished: false };
    }

    onPointerUpCell(e: CalendarGesture.Event.PointerUpCell<GestureInfo>): {
      finished: boolean;
    } {
      this.#dismissOwn(e.info);
      return {
        finished: true,
      };
    }

    onPointerUpColumn(
      e: CalendarGesture.Event.PointerUpColumn<GestureInfo>,
    ): {
      finished: boolean;
    } {
      this.#dismissOwn(e.info);
      return {
        finished: true,
      };
    }

    onFinished() {
      this.#lastItem?.dismissContextMenu();
      this.#lastItem = null;
      this.#weak = false;
    }

    #lastItem: Option<I> = null;

    #dismissOwn(info: GestureInfo): void {
      const item = info.item;
      if (item != null && this.#opts.guard(item)) item.dismissContextMenu();
      else this.#lastItem?.dismissContextMenu();
      this.#lastItem = null;
    }

    allowsConcurrentGestures(): boolean {
      return this.#weak;
    }
  }

  export class Resize<
    I extends Movable & OpensDetails,
  > extends CalendarGesture.Default<GestureInfo> {
    readonly identifier: string = "resize-event-gesture";

    #state: Option<{
      item: I;
      inner: CalendarGestureState;
      holdingWeakly: boolean;
      handle: "top" | "bottom";
      correction: DurationTime;
      anchor: DateTime<Utc>;
      wasOpen: boolean;
      lastInfo: Option<GestureInfo>;
      detailsDismissed: boolean;
    }> = null;

    readonly #opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      };
    constructor(
      opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      },
    ) {
      super();
      this.#opts = opts;
    }

    guard(info: GestureInfo): boolean {
      return info.item != null && this.#opts.guard(info.item);
    }

    // `CalendarGesture.onFinished` (tento-ui-js) passes its ambient `Option`.
    async onFinished(cancelledBy: CalendarGesture<GestureInfo> | null | undefined) {
      if (this.#state && !cancelledBy && !this.#state.holdingWeakly) {
        const state = this.#state;
        let updated: Option<Movable>;
        try {
          // Resize doesn't move horizontally — no x change.
          updated = await state.item.persistTimeChange(
            state.inner.currentTimeRange,
            null,
          );
        } finally {
          this.#opts.host.ghost.clear();
        }
        // Persist returns the renderer's own item type; re-guard to
        // prove it before re-showing details.
        if (state.wasOpen && updated != null && this.#opts.guard(updated)) {
          const info = nonnull(state.lastInfo, "a drag that closed open details recorded where");
          updated.showDetails({
            rowId: info.rowId,
            column: info.column,
            windowed: info.windowed,
          });
        }
      }
      this.#state = null;
      document.body.style.cursor = "";
    }

    canHandle(e: CalendarGesture.Event<GestureInfo>): boolean {
      const pointerDown = e.internal.pointerDown;
      if (!pointerDown) return false;

      const item = pointerDown.info.item;
      if (item == null || !this.#opts.guard(item)) return false;
      if (pointerDown.handle == null) return false;
      if (!item.isEditable) return false;

      const timeRange = item.timeRange;
      let correction: DurationTime;
      let anchor: DateTime;
      if (pointerDown.handle === "top") {
        const start = timeRange.start;
        const startFromLoc = pointerDown.info.windowed.fragment.start.withTime(
          pointerDown.position.toTimeOfDayWrapping(),
        );
        correction = start.durationSince(startFromLoc);
        anchor = timeRange.end;
      } else {
        const end = timeRange.end;
        const endFromLoc = pointerDown.info.windowed.fragment.start.withTime(
          pointerDown.position.toTimeOfDayWrapping(),
        );
        correction = end.durationSince(endFromLoc);
        anchor = timeRange.start;
      }

      this.#state = {
        item,
        inner: new CalendarGestureState(pointerDown.info, timeRange),
        holdingWeakly: false,
        handle: pointerDown.handle,
        correction,
        anchor,
        wasOpen: item.isDetailsRevealed(),
        lastInfo: null,
        detailsDismissed: false,
      };
      document.body.style.cursor = "ns-resize";

      this.#opts.host.toast("Resize");
      return true;
    }

    onPointerDown(_: CalendarGesture.Event.PointerDown<GestureInfo>): {
      finished: boolean;
    } {
      return {
        finished: false,
      };
    }

    onPointerUpCell({
      info,
      position,
    }: CalendarGesture.Event.PointerUpCell<GestureInfo>): {
      finished: boolean;
    } {
      const state = nonnull(this.#state);
      this.#updateTime(info.windowed, position, state.correction);
      this.#opts.host.ghost.update(state.item, state.inner.currentTimeRange);
      state.inner.currentDf = info.windowed.fragment;
      state.lastInfo = info;
      return {
        finished: true,
      };
    }

    onPointerUpColumn({
      info,
      position,
    }: CalendarGesture.Event.PointerUpColumn<GestureInfo>): {
      finished: boolean;
    } {
      const state = nonnull(this.#state);
      this.#updateTime(info.windowed, position, state.correction);
      state.inner.currentDf = info.windowed.fragment;
      this.#opts.host.ghost.update(state.item, state.inner.currentTimeRange);
      state.lastInfo = info;
      return {
        finished: true,
      };
    }

    onPointerMove({
      info: { windowed },
      position,
    }: CalendarGesture.Event.PointerMove<GestureInfo>): {
      shouldCapturePointer: boolean;
    } {
      const state = nonnull(this.#state);

      // Dismiss the details popover once on the first real pointer move so
      // the ghost is visible. dismissDetails for tasks/events clears the
      // ghost via existingEvent.hide(), so it must run BEFORE updateGhost
      // and only fire once — repeated dismissals across frames cause the
      // ghost to flash.
      if (!state.detailsDismissed) {
        state.item.dismissDetails();
        state.detailsDismissed = true;
      }

      this.#updateTime(windowed, position, state.correction);
      this.#opts.host.ghost.update(state.item, state.inner.currentTimeRange);
      state.inner.currentDf = windowed.fragment;

      const shouldCapturePointer = windowed.isNearEdge(position);
      return {
        shouldCapturePointer,
      };
    }

    allowsConcurrentGestures(): boolean {
      return this.#state?.holdingWeakly ?? true;
    }

    #updateTime(
      windowed: DateFragment.Windowed,
      position: Duration.Time,
      correction: DurationTime,
    ) {
      const state = nonnull(this.#state);
      const df = windowed.fragment;

      // The moving edge = the column's day-start + the pointer offset (as ms, so
      // it rolls cleanly), snapped to the 15-min grid. The bottom edge MAY extend
      // past midnight into the next day (a multi-day resize) — only a below-day-
      // start over-drag (top edge pulled above the column) is clamped up to the
      // day start. Dropping the old `Math.floor(position.toHrs / 24)` term (it
      // double-counted the day, ballooning the event ~24h); a hard end-of-day
      // clamp was wrong too — it blocked extending past midnight ("can't drag
      // past 12").
      const dayStart = df.start.toTz(df.tz);
      let raw = dayStart.add({ ms: position.toMs }).add(correction);
      if (raw.mse < dayStart.mse) raw = dayStart;
      const newTime = snapToNearestQuarterHour(raw);
      const delta = newTime.durationSince(state.anchor);

      if (delta.toMinsF >= 15) {
        state.inner.currentTimeRange = new DateTime.Range(
          state.anchor.toUtc(),
          newTime.toUtc(),
        );
      } else if (delta.toMinsF >= 0) {
        state.inner.currentTimeRange = new DateTime.Range(
          state.anchor.toUtc(),
          state.anchor.add(Duration.Time.MIN15).toUtc(),
        );
      } else if (delta.toMinsF >= -15) {
        state.inner.currentTimeRange = new DateTime.Range(
          state.anchor.sub(Duration.Time.MIN15).toUtc(),
          state.anchor.toUtc(),
        );
      } else {
        state.inner.currentTimeRange = new DateTime.Range(
          newTime.toUtc(),
          state.anchor.toUtc(),
        );
      }
    }
  }

  export class ClickToOpenDetails<
    I extends OpensDetails,
  > extends CalendarGesture.Default<GestureInfo> {
    readonly identifier: string = "click-to-open-event-gesture";
    #weak: boolean = false;

    readonly #opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      };
    constructor(
      opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
      },
    ) {
      super();
      this.#opts = opts;
    }

    guard(info: GestureInfo): boolean {
      return info.item != null && this.#opts.guard(info.item);
    }

    canHandle(e: CalendarGesture.Event<GestureInfo>): boolean {
      const handled =
        e.matchOpt({
          pointerUpCell: () => true,
        }) ?? false;
      if (handled) this.#opts.host.toast("Open Details");
      return handled;
    }

    onPointerUpCell({
      info,
    }: CalendarGesture.Event.PointerUpCell<GestureInfo>): { finished: boolean } {
      const item = info.item;
      if (item == null || !this.#opts.guard(item)) return { finished: true };
      item.showDetails({
        rowId: info.rowId,
        column: info.column,
        windowed: info.windowed,
      });

      this.#weak = true;
      return {
        finished: false,
      };
    }

    onFinished() {
      this.#weak = false;
    }

    allowsConcurrentGestures(): boolean {
      return this.#weak;
    }
  }

  export class Move<
    I extends Movable & OpensDetails,
  > extends CalendarGesture.Default<GestureInfo> {
    readonly identifier: string = "move-event-gesture";

    #state: Option<{
      item: I;
      holdingWeakly: boolean;
      inner: CalendarGestureState;
      correction: Duration.Time;
      wasOpen: boolean;
      lastInfo: Option<GestureInfo>;
      detailsDismissed: boolean;
      /** Latest pointer x-fraction across the drop column, forwarded to
       *  persist for renderers that store a horizontal position. */
      lastXFraction: Option<number>;
    }> = null;

    readonly #opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
        /**
         * Treat edge hits (the cell layer's unconditional ±4px resize
         * handles) as body hits. Renderers that compose no `Resize`
         * for this item type MUST pass true, or edge clicks on their
         * items would be dead zones. (`registerSpanGestures` derives
         * this from its `resize` declaration.)
         */
        absorbEdgeHits: boolean;
      };
    constructor(
      opts: {
        guard: ItemGuard<I>;
        host: RendererGestureHost;
        /**
         * Treat edge hits (the cell layer's unconditional ±4px resize
         * handles) as body hits. Renderers that compose no `Resize`
         * for this item type MUST pass true, or edge clicks on their
         * items would be dead zones. (`registerSpanGestures` derives
         * this from its `resize` declaration.)
         */
        absorbEdgeHits: boolean;
      },
    ) {
      super();
      this.#opts = opts;
    }

    guard(info: GestureInfo): boolean {
      return info.item != null && this.#opts.guard(info.item);
    }

    // `CalendarGesture.onFinished` (tento-ui-js) passes its ambient `Option`.
    async onFinished(cancelledBy: CalendarGesture<GestureInfo> | null | undefined) {
      if (this.#state && !cancelledBy && !this.#state.holdingWeakly) {
        const state = this.#state;
        // Same-slot release (the drag tripped the engage threshold but
        // the 15-min grid floored it back home): nothing to persist —
        // clear the ghost and restore details without a network write.
        const range = state.inner.currentTimeRange;
        const initial = state.inner.initialTimeRange;
        if (range.start.eq(initial.start) && range.end.eq(initial.end)) {
          this.#opts.host.ghost.clear();
          if (state.wasOpen && state.lastInfo) {
            state.item.showDetails({
              rowId: state.lastInfo.rowId,
              column: state.lastInfo.column,
              windowed: state.lastInfo.windowed,
            });
          }
          this.#state = null;
          return;
        }
        let updated: Option<Movable>;
        try {
          updated = await state.item.persistTimeChange(
            state.inner.currentTimeRange,
            state.lastXFraction,
          );
        } finally {
          this.#opts.host.ghost.clear();
        }
        // Persist returns the renderer's own item type; re-guard to
        // prove it before re-showing details.
        if (state.wasOpen && updated != null && this.#opts.guard(updated)) {
          const info = nonnull(state.lastInfo, "a resize that closed open details recorded where");
          updated.showDetails({
            rowId: info.rowId,
            column: info.column,
            windowed: nonnull(info.windowed, "a resize happens in a windowed column"),
          });
        }
      }
      this.#state = null;
    }

    canHandle({
      internal: { pointerDown },
    }: CalendarGesture.Event<GestureInfo>): boolean {
      if (!pointerDown) return false;

      const item = pointerDown.info.item;
      if (item == null || !this.#opts.guard(item)) return false;
      // Edge hits belong to this item type's Resize instance — unless
      // the renderer declared none and absorbs them here.
      if (pointerDown.handle != null && !this.#opts.absorbEdgeHits) {
        return false;
      }
      if (!item.isEditable) return false;

      this.#state = {
        item,
        holdingWeakly: true,
        inner: new CalendarGestureState(pointerDown.info, item.timeRange),
        correction: pointerDown.position,
        wasOpen: item.isDetailsRevealed(),
        lastInfo: null,
        detailsDismissed: false,
        lastXFraction: null,
      };
      return true;
    }

    onPointerUpCell({
      info,
      position,
      xFraction,
    }: CalendarGesture.Event.PointerUpCell<GestureInfo>): {
      finished: boolean;
    } {
      if (this.allowsConcurrentGestures()) return { finished: true };
      const state = nonnull(this.#state);
      this.#updateTime(info.windowed, position);
      this.#opts.host.ghost.update(state.item, state.inner.currentTimeRange);
      state.inner.currentDf = info.windowed.fragment;
      state.lastInfo = info;
      if (xFraction != null) state.lastXFraction = xFraction;
      return {
        finished: true,
      };
    }

    onPointerUpColumn({
      info,
    }: CalendarGesture.Event.PointerUpColumn<GestureInfo>): {
      finished: boolean;
    } {
      this.#state && (this.#state.lastInfo = info);
      return {
        finished: true,
      };
    }

    onPointerMove({
      info,
      delta,
      position,
      xFraction,
    }: CalendarGesture.Event.PointerMove<GestureInfo>): {
      shouldCapturePointer: boolean;
    } {
      const state = nonnull(this.#state);
      if (xFraction != null) state.lastXFraction = xFraction;
      // Weak hold releases when the cursor crosses meaningful ground —
      // a >5 min cumulative vertical delta (4px at 48px/hr — standard
      // mouse click slop; the drag feels dead until the ghost engages,
      // so this stays small) OR a date change (cross-column drag is
      // exactly this case). The receiving column's `windowed` arrives
      // via the manager's pointerId routing, so cross-column motion
      // falls out for free. The same-slot guard in `onFinished` keeps
      // a sloppy click that trips this from issuing a no-op persist.
      state.holdingWeakly =
        state.holdingWeakly &&
        Math.abs(delta.toMins) < 5 &&
        info.windowed.fragment.date.equals(
          state.inner.info.windowed.fragment.date,
        );
      if (state.holdingWeakly) {
        return {
          shouldCapturePointer: false,
        };
      }
      // Dismiss the details popover once on the first real pointer
      // move (same once-guard as Resize) — repeated dismissals per
      // frame re-run presenter/global-state teardown for nothing.
      if (!state.detailsDismissed) {
        state.item.dismissDetails();
        state.detailsDismissed = true;
      }
      this.#updateTime(info.windowed, position);
      this.#opts.host.ghost.update(state.item, state.inner.currentTimeRange);
      state.inner.currentDf = info.windowed.fragment;
      return {
        shouldCapturePointer: false,
      };
    }

    allowsConcurrentGestures(): boolean {
      return this.#state?.holdingWeakly ?? true;
    }

    #updateTime(
      windowed: DateFragment.Windowed,
      position: Duration.Time,
    ) {
      const state = nonnull(this.#state);
      const df = windowed.fragment;
      const delta = position.sub(state.correction);

      const tod = state.inner.initialTimeRange.start.toTz(df.tz).time;
      const day = df.date;

      // Snap the move start to the NEAREST 15-min slot (matches Resize), so
      // dropped events land on clean :00/:15/:30/:45 boundaries. Flooring
      // turned a one-hour drag that came in a hair short into +45 min.
      // The date carried by `add({ ms })` is preserved.
      const raw = day
        .withTime(tod)
        .withTz(df.tz)
        .add({ ms: delta.toMs });
      const startTime = snapToNearestQuarterHour(raw);

      const duration = state.inner.initialTimeRange.duration;
      Tracing.admin(
        "modified-event",
        {
          delta: delta.dbg(),
          durationMs: duration.dbg(),
          start: startTime.rfc3339(),
        },
        true,
      );

      state.inner.currentTimeRange = new DateTime.Range(
        startTime,
        startTime.add(duration),
      ).toUtc();
    }
  }

  /**
   * The single surface-level dismissal gesture: an empty-column click
   * settles the world. Every live popover/menu/pending form —
   * regardless of renderer — registers a global state, so engagement
   * is just "is anything open" and the action is the coordinator
   * sweep. Registered ONCE per gesture surface.
   */
  export class Dismiss extends CalendarGesture.Default<GestureInfo> {
    readonly identifier: string = "dismiss-gesture";

    readonly #host: CalendarGestureHost;
    constructor(host: CalendarGestureHost) {
      super();
      this.#host = host;
    }

    canHandle(e: CalendarGesture.Event<GestureInfo>): boolean {
      return (
        e.matchOpt({
          pointerUpColumn: (up) => {
            if (!this.#host.hasOpenState()) return false;
            // A click on the pending ghost's own span (the create
            // form's drafted event) is part of the pending
            // interaction, not a dismissal of it.
            if (this.#landsOnGhost(up)) return false;
            return true;
          },
        }) ?? false
      );
    }

    #landsOnGhost(
      up: CalendarGesture.Event.PointerUpColumn<GestureInfo>,
    ): boolean {
      const ghost = this.#host.currentGhost();
      if (!ghost) return false;
      return ghostContainsPointerEvent(ghost, up.info.windowed, up.position);
    }

    onPointerUpColumn(): { finished: boolean } {
      this.#host.closeAll();
      return { finished: true };
    }

    allowsConcurrentGestures(): boolean {
      return true;
    }
  }
}
